// WS5 PR3.3c — Cinematic auto-tour sequencer.
//
// Modulo PURO: sequenzia `flyToCamera` (iniettato) attraverso le prime N
// viewbox della scena con dwell intermedio + FOV preset ciclico. NON
// possiede adapter ne' controls: l'orchestrator wrappa `animator.flyToCamera`
// e lo passa come dipendenza. Tutti i timer sono iniettabili per test
// (fake-timers). Abort-safe via AbortSignal del caller.
//
// Cfr. docs/PLAN_WS5_first_person_viewbox.md §11.
//
// Decisioni:
//   - Cap 5 viewbox max (oltre, jump finale a `defaultArrayIdx`).
//   - FOV ciclico {45, 55, 60, 65} deg + jitter ±0 di default (deterministico
//     per test; orchestrator puo' attivare jitter ±5° via opzione).
//   - reduced-motion → fly istantaneo (durationMs=0) ma DWELL preservato
//     (l'utente vuole VEDERE le stanze; reduced-motion vale per la rotazione
//     non per il pacing narrativo).
//   - Abort durante fly o dwell → land istantaneo (200ms) su default viewbox;
//     l'orchestrator si occupa di reset modality + pointer-lock available.
//   - Ordine visita: viewboxes[0..min(5,N)-1] in array order. defaultArrayIdx
//     e' il target FINALE (jump al termine, 400ms fly veloce).

import type { ModelViewbox } from "../api/types.js";
import type { CameraTarget } from "../tour/animator.js";

/** Preset FOV cinematic (deg). Ciclico per viewbox visitata. Allineato Unity
 *  preset PLAN §11.2. */
export const CINEMATIC_FOV_PRESET_DEG = [45, 55, 60, 65] as const;

/** Cap viewbox visitate dal tour. PLAN §11.2: max 20-25s totali → 5 * (2+2.5) = 22.5s. */
export const CINEMATIC_MAX_VIEWBOXES = 5;

/** Durata fly tra viewbox (ms). PLAN §11.2: 2.0s. */
export const CINEMATIC_FLY_MS = 2000;

/** Dwell stazionario per viewbox (ms). PLAN §11.2: 2.5s. */
export const CINEMATIC_DWELL_MS = 2500;

/** Fly veloce finale: jump al defaultArrayIdx dopo l'ultima viewbox del cap.
 *  Conservativo: 400ms = transizione percepita ma rapida. */
export const CINEMATIC_FINAL_LAND_MS = 400;

/** Fly veloce di abort: PLAN §11.3 "fade-out 200ms → entra in default_viewbox". */
export const CINEMATIC_ABORT_LAND_MS = 200;

/** Phase corrente, emessa via onPhase callback (UI skip button, mutex). */
export type CinematicPhase =
	| "idle"
	| "flying" // fly verso una viewbox in corso
	| "dwelling" // sosta su viewbox corrente
	| "landing" // fly finale verso default (post-cap o abort)
	| "completed"
	| "aborted";

export interface CinematicTourDeps {
	/** Esegue il fly. Wrappa `animator.flyToCamera`. Risolve a fine animazione
	 *  o reject DOMException('aborted','AbortError') su signal abort. */
	flyTo: (
		from: CameraTarget,
		to: CameraTarget,
		durationMs: number,
		signal: AbortSignal,
	) => Promise<void>;
	/** Scheduler iniettabile per dwell (test con fake-timers). Default = globalThis. */
	scheduler?: {
		setTimeout: (cb: () => void, ms: number) => unknown;
		clearTimeout: (handle: unknown) => void;
	};
	/** Random iniettabile per FOV jitter deterministico in test. Default = Math.random. */
	random?: () => number;
}

export interface CinematicTourOptions {
	/** Viewbox della scena. Il tour visita le prime min(N, MAX_VIEWBOXES). */
	viewboxes: readonly ModelViewbox[];
	/** Index dell'array viewboxes su cui atterrare al termine (post-cap o abort).
	 *  Caller deve aver gia' risolto default_viewbox_index → arrayIdx via
	 *  arrayIdxFromNumericIndex. Se fuori range → fallback 0. */
	defaultArrayIdx: number;
	/** Camera corrente all'avvio (usata come `from` del primo fly). */
	startCamera: CameraTarget;
	/** prefers-reduced-motion → fly istantaneo (durata 0); dwell preservato. */
	reducedMotion: boolean;
	/** AbortSignal del caller (orchestrator.cinematicAbort). */
	signal: AbortSignal;
	/** Phase listener: notificato per skip button + mutex (orchestrator).
	 *  currentArrayIdx = idx dell'ultima viewbox engaged (flying/dwelling) o
	 *  null se phase=idle/aborted/landing-pre-target. */
	onPhase?: (phase: CinematicPhase, currentArrayIdx: number | null) => void;
	/** Jitter FOV ±degrees (PLAN §11.2 "jitter ±5°"). Default 0 (deterministic).
	 *  Quando >0, jitter = (random()-0.5)*2*jitterDeg → uniforme [-jitter,+jitter]. */
	fovJitterDeg?: number;
	/** WS5 PR3.3c (Codex W3): callback opzionale per leggere la camera REALE
	 *  al momento dell'abort. Quando fornito, il fly di landing parte dalla
	 *  posizione corrente (non da `prevCamera` logico, che durante un fly
	 *  abortato sarebbe il TARGET precedente — produce snap visibile).
	 *  Orchestrator passa `() => currentCamera()`. */
	getCurrentCamera?: () => CameraTarget;
	/** WS5 PR3.3c (Codex W3): signal separato per abortare il landing finale
	 *  (es. dispose / XR takeover durante land). `opts.signal` da solo non
	 *  basta: dopo l'abort entra in landAtDefault con un signal aborted →
	 *  abortable flyTo rejecterebbe subito → snap. Quando fornito, il caller
	 *  puo' abortire il land tramite questo signal. Default: land usa
	 *  AbortController interno non cancellabile (behavior PR3.3c base). */
	landSignal?: AbortSignal;
}

/** Forward vector da yaw/pitch (convention ONIRI: y-up, yaw=0 → -Z forward).
 *  Allineato orchestrator selectViewbox:2779-2781. */
function forwardFromYawPitch(
	yaw: number,
	pitch: number,
): [number, number, number] {
	const cosP = Math.cos(pitch);
	return [
		-Math.sin(yaw) * cosP,
		Math.sin(pitch),
		-Math.cos(yaw) * cosP,
	];
}

/** Costruisce CameraTarget per viewbox visitata dal tour. FOV viene dal preset
 *  ciclico (NON da `vb.default_camera.fov`), per dare ritmo cinematic alle
 *  diverse stanze. cyclicIdx = posizione nella sequenza visit (0,1,2,...). */
export function cinematicTargetForViewbox(
	vb: ModelViewbox,
	cyclicIdx: number,
	fovJitterDeg: number,
	random: () => number,
): CameraTarget {
	const cam = vb.default_camera;
	const fwd = forwardFromYawPitch(cam.yaw, cam.pitch);
	const baseFov =
		CINEMATIC_FOV_PRESET_DEG[
			cyclicIdx % CINEMATIC_FOV_PRESET_DEG.length
		] ?? 55;
	const jitter =
		fovJitterDeg > 0 ? (random() - 0.5) * 2 * fovJitterDeg : 0;
	return {
		pos: [cam.pos[0], cam.pos[1], cam.pos[2]],
		lookAt: [cam.pos[0] + fwd[0], cam.pos[1] + fwd[1], cam.pos[2] + fwd[2]],
		fov: baseFov + jitter,
	};
}

/** Costruisce CameraTarget "land" per atterraggio finale (post-cap o abort).
 *  Usa FOV autoriale `vb.default_camera.fov` (rad→deg) — NON il preset cinematic
 *  — perche' lasciamo l'utente nella stanza con la sua FOV "corretta". */
export function landingTargetForViewbox(vb: ModelViewbox): CameraTarget {
	const cam = vb.default_camera;
	const fwd = forwardFromYawPitch(cam.yaw, cam.pitch);
	return {
		pos: [cam.pos[0], cam.pos[1], cam.pos[2]],
		lookAt: [cam.pos[0] + fwd[0], cam.pos[1] + fwd[1], cam.pos[2] + fwd[2]],
		fov: (cam.fov * 180) / Math.PI,
	};
}

/** Helper sleep con abort. Risolve normalmente al timeout, reject su abort. */
function sleepAbortable(
	ms: number,
	signal: AbortSignal,
	scheduler: NonNullable<CinematicTourDeps["scheduler"]>,
): Promise<void> {
	if (signal.aborted) {
		return Promise.reject(new DOMException("aborted", "AbortError"));
	}
	return new Promise<void>((resolve, reject) => {
		let handle: unknown = null;
		const onAbort = (): void => {
			if (handle !== null) scheduler.clearTimeout(handle);
			signal.removeEventListener("abort", onAbort);
			reject(new DOMException("aborted", "AbortError"));
		};
		signal.addEventListener("abort", onAbort, { once: true });
		handle = scheduler.setTimeout(() => {
			signal.removeEventListener("abort", onAbort);
			resolve();
		}, ms);
	});
}

function isAbortError(err: unknown): boolean {
	if (err instanceof DOMException && err.name === "AbortError") return true;
	if (err instanceof Error && err.name === "AbortError") return true;
	return false;
}

const DEFAULT_SCHEDULER: NonNullable<CinematicTourDeps["scheduler"]> = {
	setTimeout: (cb, ms) =>
		(globalThis as { setTimeout: typeof setTimeout }).setTimeout(cb, ms),
	clearTimeout: (h) =>
		(globalThis as { clearTimeout: typeof clearTimeout }).clearTimeout(
			h as ReturnType<typeof setTimeout>,
		),
};

/** Sequenzia il cinematic tour. Ritorna { aborted: boolean } a fine sequenza
 *  (completato o abortato; non rilancia AbortError). Errori non-abort durante
 *  fly bubblano (caller orchestrator catch + log).
 *
 *  Garanzie:
 *  - Emette onPhase: idle → flying[i] → dwelling[i] → ... → landing → completed/aborted.
 *  - Su signal.aborted DURANTE fly o dwell: catch AbortError → emette landing
 *    + esegue fly veloce (200ms) verso defaultArrayIdx, poi emette 'aborted'.
 *  - Su signal.aborted PRIMA del primo step (loop break sincrono, no throw):
 *    soft-abort path → emette landing + completed (NON aborted). Razionale UX:
 *    l'utente non ha mai visto un flying[i], "aborted" sarebbe confusing.
 *    Il caller riceve aborted=false ma il signal e' ancora .aborted.
 *  - Su completion (cap raggiunto): se ultimo visitato !== defaultArrayIdx,
 *    fly finale 400ms verso default; poi emette 'completed'.
 *  - Cap = min(viewboxes.length, MAX_VIEWBOXES). Se viewboxes.length === 0:
 *    no-op (idle → completed). */
export async function runCinematicTour(
	deps: CinematicTourDeps,
	opts: CinematicTourOptions,
): Promise<{ aborted: boolean }> {
	const scheduler = deps.scheduler ?? DEFAULT_SCHEDULER;
	const random = deps.random ?? Math.random;
	const fovJitter = opts.fovJitterDeg ?? 0;
	const emit = (phase: CinematicPhase, idx: number | null): void => {
		opts.onPhase?.(phase, idx);
	};

	const N = opts.viewboxes.length;
	if (N === 0) {
		emit("completed", null);
		return { aborted: false };
	}

	const cap = Math.min(N, CINEMATIC_MAX_VIEWBOXES);
	const flyMs = opts.reducedMotion ? 0 : CINEMATIC_FLY_MS;

	// Risolve defaultArrayIdx con fallback 0 se fuori range.
	const safeDefaultIdx =
		opts.defaultArrayIdx >= 0 && opts.defaultArrayIdx < N
			? opts.defaultArrayIdx
			: 0;

	let prevCamera = opts.startCamera;
	let lastVisitedIdx = -1;

	const landAtDefault = async (): Promise<void> => {
		const targetVb = opts.viewboxes[safeDefaultIdx];
		if (targetVb === undefined) return;
		const landTarget = landingTargetForViewbox(targetVb);
		const landMs = opts.reducedMotion
			? 0
			: opts.signal.aborted
				? CINEMATIC_ABORT_LAND_MS
				: CINEMATIC_FINAL_LAND_MS;
		// WS5 PR3.3c (Codex W3a): start from REAL camera, not logical prevCamera.
		// Durante un fly abortato, prevCamera e' il target precedente: usarlo
		// come `from` produrrebbe uno snap visibile. opts.getCurrentCamera()
		// passato dall'orchestrator come `() => currentCamera()` legge la
		// posizione effettiva post-abort. Fallback prevCamera quando assente
		// (test, smoke).
		const fromCamera =
			opts.getCurrentCamera !== undefined
				? opts.getCurrentCamera()
				: prevCamera;
		// WS5 PR3.3c (Codex W3b): landSignal opzionale per cancellare anche il
		// land (dispose, XR takeover durante landing). Default: AbortController
		// interno non cancellabile (behavior PR3.3c base).
		const landCtrl = new AbortController();
		const landSignal = opts.landSignal ?? landCtrl.signal;
		emit("landing", safeDefaultIdx);
		try {
			await deps.flyTo(fromCamera, landTarget, landMs, landSignal);
		} catch (err) {
			if (!isAbortError(err)) throw err;
		}
		prevCamera = landTarget;
	};

	try {
		for (let i = 0; i < cap; i++) {
			if (opts.signal.aborted) break;
			const vb = opts.viewboxes[i];
			if (vb === undefined) continue;
			const target = cinematicTargetForViewbox(vb, i, fovJitter, random);

			emit("flying", i);
			await deps.flyTo(prevCamera, target, flyMs, opts.signal);
			prevCamera = target;
			lastVisitedIdx = i;

			if (opts.signal.aborted) break;
			emit("dwelling", i);
			await sleepAbortable(CINEMATIC_DWELL_MS, opts.signal, scheduler);
		}

		// Atterraggio: se ultimo visitato != default, oppure se nessuno visitato,
		// fly finale verso default. Se ultimo == default, gia' siamo li' → skip
		// landing fly ma emettiamo comunque la phase per consistency UI.
		if (lastVisitedIdx !== safeDefaultIdx) {
			await landAtDefault();
		} else {
			emit("landing", safeDefaultIdx);
		}
		emit("completed", safeDefaultIdx);
		return { aborted: false };
	} catch (err) {
		if (!isAbortError(err)) {
			// Errore non-abort durante fly: emettiamo aborted (mutex cleanup)
			// e rilanciamo. Caller orchestrator log + cleanup.
			emit("aborted", null);
			throw err;
		}
		// Abort path: land veloce su default + emit aborted.
		await landAtDefault();
		emit("aborted", safeDefaultIdx);
		return { aborted: true };
	}
}
